import { useEffect, useState } from "react";
import {
  FaPhoneAlt,
  FaEnvelope,
  FaHandshake,
  FaLaptop,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaRegCommentDots,
  FaUser,
  FaExchangeAlt,
  FaHistory,
  FaCalendarAlt,
  FaPaperclip,
  FaFileDownload,
  FaEye,
  FaFilePdf,
  FaFileAlt,
} from "react-icons/fa";

import api from "../../api/axios";
import "../../assets/css/FollowUpLead/FollowUpLeadList.css";
import DealDocumentPreviewModal from "../Deals/DealDocumentPreviewModal";

export default function FollowUpList({
  leadId,
  dealId,
  type = "lead",
  refreshKey = 0,
}) {
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);

  const fetchFollowups = async () => {
    if (type === "lead" && !leadId) return;
    if (type === "deal" && !dealId) return;

    try {
      setLoading(true);

      const url =
        type === "deal"
          ? `/deal-followup/deal/${dealId}`
          : `/lead-followup/lead/${leadId}`;

      const response = await api.get(url);

      setFollowups(
        Array.isArray(response?.data)
          ? response.data
          : []
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFollowups();
  }, [leadId, dealId, type, refreshKey]);

  const getStatusClass = (status = "") => {
    const value = status.toLowerCase();

    if (value.includes("completed") || value.includes("converted") || value.includes("won") || value.includes("confirmation")) return "success";
    if (value.includes("proposal") || value.includes("review") || value.includes("pending") || value.includes("vendor") || value.includes("invoicing") || value.includes("negotiation")) return "warning";
    if (value.includes("lost") || value.includes("cancel")) return "danger";
    return "primary";
  };

  const getIcon = (itemType = "", status = "") => {
    const t = (itemType || "").toLowerCase();
    const s = (status || "").toLowerCase();

    if (t === "call") return <FaPhoneAlt />;
    if (t === "email") return <FaEnvelope />;
    if (t === "meeting") return <FaHandshake />;
    if (t === "demo") return <FaLaptop />;

    if (s.includes("completed") || s.includes("won")) return <FaCheckCircle />;
    if (s.includes("warning") || s.includes("proposal")) return <FaExclamationTriangle />;
    return <FaRegCommentDots />;
  };

  if (loading) {
    return (
      <div className="flw-wrap">
        <div className="flw-header">
          <h3>Followups</h3>
          <span>{followups.length}</span>
        </div>
        <div className="flw-state">Loading timeline...</div>
      </div>
    );
  }

  return (
    <div className="flw-wrap">
      <div className="flw-header">
        <h3>Followups</h3>
        <span>{followups.length}</span>
      </div>

      <div className="flw-timeline">
        {followups.length > 0 ? (
          followups.map((item, index) => {
            return (
              <div key={item.id || index} className="flw-row">
                {/* LEFT TIMELINE */}
                <div className="flw-left">
                  <div className="flw-icon">
                    {getIcon(item.type, item.status)}
                  </div>
                  {index !== followups.length - 1 && <div className="flw-line" />}
                </div>

                {/* CARD */}
                <div className="flw-card">
                  <div className="flw-top">
                    <div className="flw-top-tags">
                      <span className={`flw-badge ${getStatusClass(item.status)}`}>
                        {item.status || "Activity"}
                      </span>

                      {item.type && (
                        <span className="flw-type-pill">
                          {item.type}
                        </span>
                      )}
                    </div>

                    <span
                      className="flw-assigned-pill"
                      title="Current Assigned User"
                    >
                      <FaUser size={10} style={{ color: "#2563eb" }} />
                      Assign To: <strong>{item.assigned_to_name || item.assigned_user_name || "Unassigned"}</strong>
                    </span>
                  </div>

                  <div className="flw-message">
                    {item.remarks || "No remarks provided"}
                  </div>

                  {/* INLINE ASSIGNMENT & ACTIVITY EVENTS */}
                  {item.activities && item.activities.length > 0 && (
                    <div className="flw-activities-box">
                      <div className="flw-activities-title">
                        <FaHistory size={10} /> Activity Updates
                      </div>

                      {item.activities.map((act) => {
                        if (act.activity_type === "ASSIGNMENT_CHANGED") {
                          const oldName = act.old_assigned_to?.name || "Unassigned";
                          const newName = act.new_assigned_to?.name || "Unassigned";
                          return (
                            <div key={act.id} className="flw-activity-item">
                              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                <FaExchangeAlt size={10} style={{ color: "#2563eb" }} />
                                <span>
                                  Reassigned: <strong>{oldName}</strong> → <strong style={{ color: "#2563eb" }}>{newName}</strong>
                                </span>
                              </span>
                              <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                                {act.created_by ? `by ${act.created_by}` : ""} • {new Date(act.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          );
                        }

                        if (act.activity_type === "STATUS_CHANGED") {
                          return (
                            <div key={act.id} className="flw-activity-item">
                              <span>
                                Stage changed: <strong>{act.old_value}</strong> → <strong>{act.new_value}</strong>
                              </span>
                              <span style={{ fontSize: "11px", color: "#94a3b8" }}>
                                {new Date(act.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          );
                        }

                        return null;
                      })}
                    </div>
                  )}

                  {/* ATTACHED DOCUMENTS */}
                  {item.documents && item.documents.length > 0 && (
                    <div className="flw-docs-attachment-box">
                      <div className="flw-docs-attachment-header">
                        <FaPaperclip size={11} style={{ color: "#0284c7" }} />
                        <span>Attached Documents ({item.documents.length})</span>
                      </div>
                      <div className="flw-docs-list">
                        {item.documents.map((doc) => (
                          <div key={doc.id} className="flw-doc-pill">
                            <div className="flw-doc-pill-left">
                              <span className={`flw-doc-tag ${doc.doc_type?.toLowerCase()}`}>
                                {doc.doc_type === "PO" ? "PO" : (doc.doc_type === "INVOICE" ? "Invoice" : doc.doc_type)}
                              </span>
                              <span className="flw-doc-name" title={doc.original_filename}>
                                {doc.original_filename}
                              </span>
                              {doc.doc_number && (
                                <span className="flw-doc-meta-badge">
                                  #{doc.doc_number}
                                </span>
                              )}
                              {doc.amount !== undefined && doc.amount !== null && doc.amount > 0 && (
                                <span className="flw-doc-meta-badge amount">
                                  ₹{Number(doc.amount).toLocaleString("en-IN")}
                                </span>
                              )}
                              {doc.file_size_formatted && (
                                <span className="flw-doc-size">
                                  {doc.file_size_formatted}
                                </span>
                              )}
                            </div>

                            <div className="flw-doc-pill-actions">
                              <button
                                type="button"
                                className="flw-doc-action-btn preview"
                                onClick={() => setPreviewDoc(doc)}
                                title="Preview Document"
                              >
                                <FaEye size={11} />
                                <span>Preview</span>
                              </button>
                              <button
                                type="button"
                                className="flw-doc-action-btn download"
                                onClick={() => {
                                  const baseURL = process.env.REACT_APP_API_BASE_URL || "/api";
                                  window.open(`${baseURL}/deal/documents/${doc.id}/download`, "_blank");
                                }}
                                title="Download Document"
                              >
                                <FaFileDownload size={11} />
                                <span>Download</span>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flw-footer">
                    <div className="flw-user">
                      <FaClock />
                      {item.created_at
                        ? new Date(item.created_at).toLocaleString()
                        : "-"}
                    </div>

                    <div className="flw-date" title="Author who created this note">
                      <FaUser />
                      Created By: <span>{item.created_by_name || item.created_by || "System"}</span>
                    </div>

                    {item.next_followup_date && (
                      <div className="flw-next-badge" title="Next follow-up target date">
                        <FaCalendarAlt />
                        Next: {new Date(item.next_followup_date).toLocaleDateString()} {item.next_followup_time ? `at ${item.next_followup_time}` : ""}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="flw-empty-container">
            <div className="flw-empty-icon">
              <FaRegCommentDots />
            </div>
            <div className="flw-empty-title">No Activities found</div>
            <p className="flw-empty-desc">
              Log communication notes or schedule next interactions to track progress.
            </p>
          </div>
        )}
      </div>

      {previewDoc && (
        <DealDocumentPreviewModal
          isOpen={Boolean(previewDoc)}
          document={previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </div>
  );
}